import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { verifyPassword, getUserSession, saveUserSession, clearUserSession, getRoleLabel } from '../utils/auth';

export default function AuthWrapper({ children }) {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [userRole, setUserRole] = useState(null);
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		// Check if user is already authenticated
		const session = getUserSession();
		if (session.isValid) {
			setIsAuthenticated(true);
			setUserRole(session.role);
		}
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError('');

		// Simulate network delay for UI feedback
		setTimeout(async () => {
			const result = await verifyPassword(password);

			if (result.isValid) {
				// Use crypto-js md5 for consistency with auth.js
				const md5 = (await import('crypto-js/md5')).default;
				const enc = (await import('crypto-js')).enc;
				const passwordHash = md5(password).toString(enc.Hex);

				saveUserSession(result.role, passwordHash);
				setIsAuthenticated(true);
				setUserRole(result.role);
				setPassword('');
			} else {
				setError('Incorrect password');
			}
			setLoading(false);
		}, 600);
	};

	const handleLogout = () => {
		clearUserSession();
		setIsAuthenticated(false);
		setUserRole(null);
		setPassword('');
	};

	if (!isAuthenticated) {
		return (
			<div className='min-h-screen flex items-center justify-center bg-gray-50 px-4'>
				<div className='max-w-md w-full space-y-8'>
					<div>
						<h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
							Asana <span className='text-primary'>Time Tracking</span>
						</h2>
						<p className='mt-2 text-center text-sm text-gray-600'>
							Please enter the password to access the dashboard
						</p>
					</div>

					<div className='bg-white p-8 rounded-lg shadow-md'>
						<form className='space-y-6' onSubmit={handleSubmit}>
							{error && (
								<div className='p-3 rounded-md bg-red-50 flex items-start'>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="text-red-500 mt-0.5 mr-2 flex-shrink-0 h-5 w-5"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<circle cx="12" cy="12" r="10"></circle>
										<line x1="12" y1="8" x2="12" y2="12"></line>
										<line x1="12" y1="16" x2="12.01" y2="16"></line>
									</svg>
									<span className='text-sm text-red-700'>{error}</span>
								</div>
							)}

							<div>
								<label
									htmlFor='password'
									className='block text-sm font-medium text-gray-700'
								>
									Password
								</label>
								<div className='mt-1 relative'>
									<div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-5 w-5 text-gray-400"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										>
											<rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
											<path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
										</svg>
									</div>
									<input
										id='password'
										name='password'
										type='password'
										autoComplete='current-password'
										required
										value={password}
										onChange={e => setPassword(e.target.value)}
										className='appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm'
										placeholder='Enter dashboard password'
										disabled={loading}
									/>
								</div>
							</div>

							<div>
								<button
									type='submit'
									className='group relative w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
									disabled={loading}
								>
									{loading ? (
										<>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="animate-spin mr-2 h-5 w-5"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<line x1="12" y1="2" x2="12" y2="6"></line>
												<line x1="12" y1="18" x2="12" y2="22"></line>
												<line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
												<line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
												<line x1="2" y1="12" x2="6" y2="12"></line>
												<line x1="18" y1="12" x2="22" y2="12"></line>
												<line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
												<line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
											</svg>
											Logging in...
										</>
									) : (
										<>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="mr-2 h-5 w-5"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
											>
												<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
												<polyline points="10 17 15 12 10 7"></polyline>
												<line x1="15" y1="12" x2="3" y2="12"></line>
											</svg>
											Log in
										</>
									)}
								</button>
							</div>
						</form>
					</div>

					<div className='text-center mt-4'>
						<p className='text-sm text-gray-600'>
							Contact your administrator if you need access to the dashboard.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ zoom: '0.9' }}>
			<div className='p-4 flex justify-between items-center'>
				<div>
					<h1 className='text-3xl font-bold text-gray-900'>
						Asana <span className='text-primary'>Time Tracking</span>
					</h1>
					{userRole && (
						<p className='text-sm text-gray-500 mt-1'>
							Logged in as: <span className='font-semibold'>{getRoleLabel(userRole)}</span>
						</p>
					)}
				</div>
				<button
					onClick={handleLogout}
					className='flex items-center px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
				>
					Logout
				</button>
			</div>
			{React.cloneElement(children, { userRole })}
		</div>
	);
}

AuthWrapper.propTypes = {
	children: PropTypes.node.isRequired,
};
